// Use the following when you are testing live in browser
//import xmldom from '../web_modules/xmldom.js';
// Use the following in all other circumstances
import xmldom from '@xmldom/xmldom';
import { transformCoordinates, multiply3x3, IDENTITY_MATRIX } from './math.js';
export {
  stringToDoc,
  docToString,
  normalize,
  getDocumentMetadata,
  applySubstitutionClasses,
  _trimBraceGroup,
  _getTopLevelBraceGroups,
  _mergeClasses,
};

function stringToDoc(circuit) {
  return new xmldom.DOMParser().parseFromString(circuit, 'text/xml');
}
function docToString(doc) {
  var serializer = new xmldom.XMLSerializer();
  //HACK: Undocumented isHtml parameter seems to help avoid <svg:xxx> prefixing that can be harmful.
  return serializer.serializeToString(doc);
}
// Core processing of normalize, given a document already open on the circuit
function normalize(document) {
  var defaultNamespaces = ['', 'svg', 'xlink'];
  const namespaceMap = Object.keys(document.documentElement._nsMap)
    .filter((key) => !defaultNamespaces.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: document.documentElement._nsMap[key],
      };
    }, {});

  // Remove target top-level child elements
  let removableNodeNames = ['defs', 'metadata', 'sodipodi', 'namedview', 'inkscape'];
  for (var i = document.documentElement.childNodes.length - 1; i >= 0; i--) {
    var node = document.documentElement.childNodes[i];

    //deletes the <defs>, <metadata>, <sodipodi> element blocks.
    if (removableNodeNames.includes(node.localName)) {
      document.documentElement.removeChild(node);
    }
  }

  // Remove all attributes with targeted namespaces
  let elements = document.getElementsByTagName('*');
  for (let elementIndex = elements.length - 1; elementIndex >= 0; elementIndex--) {
    let node = elements[elementIndex];
    // Walk the NamedNodeMap backwards so deletions don't mess up where you are
    for (let attributeIndex = node.attributes.length - 1; attributeIndex >= 0; attributeIndex--) {
      let attribute = node.attributes[attributeIndex];
      if (attribute.prefix !== null && namespaceMap[attribute.prefix] === attribute.namespaceURI) {
        node.removeAttributeNS(attribute.namespaceURI, attribute.localName);
      }
    }
  }
}

function applySubstitutionClasses(document) {
  let elements = document.getElementsByTagName('tspan');
  for (let elementIndex = elements.length - 1; elementIndex >= 0; elementIndex--) {
    let content = elements[elementIndex].textContent;
    let classes = _getClassesForSubstitutions(content);
    if (classes.length > 0) {
      let existingClasses = elements[elementIndex].getAttribute('class');
      elements[elementIndex].setAttribute('class', _mergeClasses(existingClasses, classes));
    }
  }
}

function _getClassesForSubstitutions(elementText) {
  let groups = _getTopLevelBraceGroups(elementText);
  return groups.map((value) => 'test-' + _trimBraceGroup(value));
}

function _mergeClasses(existing, newClasses) {
  return (existing ? existing : '')
    .trim()
    .split(/\s+/)
    .filter((clazz) => !newClasses.includes(clazz))
    .concat(newClasses)
    .join(' ')
    .trim();
}

function _trimBraceGroup(text) {
  return text
    .trim()
    .replace(/[^-_ \w\d]*/g, '')
    .replace(/\s+/g, '-');
}

function _getTopLevelBraceGroups(text) {
  let groups = [];
  let first = 0,
    last = 0,
    depth = 0;
  for (let index = 0; index < text.length; index++) {
    switch (text[index]) {
      case '{':
        if (depth === 0) {
          first = index;
        }
        depth++;
        break;
      case '}':
        depth--;
        if (depth < 0) {
          throw 'Unbalanced braces in substitution';
        }
        if (depth === 0) {
          last = index;
          groups.push(text.substring(first, last + 1));
        }
    }
  }
  return groups;
}

function getDocumentMetadata(document) {
  let width = _coordinateToPixels(document.documentElement.getAttribute('width'));
  let height = _coordinateToPixels(document.documentElement.getAttribute('height'));
  let viewBox = document.documentElement.getAttribute('viewBox') ?? `0 0 ${width} ${height}`;
  let preserve = document.documentElement.getAttribute('preserveAspectRatio') ?? 'xMidYMid meet';
  let transformMatrix = _parseTransformAsMatrix(document.documentElement.getAttribute('transform'));
  let viewboxMatrix = _viewboxMatrix(
    [0, 0, width, height],
    viewBox.split(/[\s]+/).map((value) => parseFloat(value)),
    preserve.split(/[\s]+/)
  );
  let connectionPoints = [];
  let connectionPointClasses = ['primary-in', 'primary-out', 'secondary-in', 'secondary-out'];
  let elements = document.getElementsByTagName('g');
  for (let elementIndex = elements.length - 1; elementIndex >= 0; elementIndex--) {
    let group = elements[elementIndex];
    if (group.hasAttribute('class')) {
      for (let clazz of group.getAttribute('class').split(/[\s]+/)) {
        if (connectionPointClasses.includes(clazz)) {
          if (_isVisibleElement(group)) {
            let defaultConnectionGroup = clazz.includes('-in') ? 'IN' : 'OUT';
            let connectionPoint = _collectConnectionPoint(
              document,
              group,
              clazz,
              [transformMatrix, viewboxMatrix],
              defaultConnectionGroup
            );
            if (connectionPoint) {
              connectionPoints.push(connectionPoint);
            }
          }
        }
      }
    }
  }
  return {
    docWidth: width,
    docHeight: height,
    matrix: multiply3x3(transformMatrix, viewboxMatrix),
    connectionPoints: connectionPoints,
  };
}

function _isVisibleElement(elem) {
  let result = elem.getAttribute('display') !== 'none';
  if (result && elem.parentNode && elem.parentNode.nodeType === 1) {
    result = _isVisibleElement(elem.parentNode);
  }
  return result;
}

function _parseTranslateAsMatrix(...args) {
  if (args.length !== 2) return;
  let x = parseFloat(args[0]);
  let y = parseFloat(args[1]);
  if (isNaN(x) || isNaN(y)) return;
  return [1, 0, x, 0, 1, y, 0, 0, 1];
}
function _parseRotateAsMatrix() {
  if (arguments.length !== 1 && arguments.length != 3) return;
  let angle = parseFloat(arguments[0]);
  if (isNaN(angle)) return;
  angle = (angle * Math.PI) / 180;
  if (arguments.length === 3) {
    let x = parseFloat(arguments[1]);
    let y = parseFloat(arguments[2]);
    if (isNaN(x) || isNaN(y)) return;
    return [Math.cos(angle), -Math.sin(angle), 0, Math.sin(angle), Math.cos(angle), 0, 0, 0, 1];
  } else {
    return [Math.cos(angle), -Math.sin(angle), 0, Math.sin(angle), Math.cos(angle), 0, 0, 0, 1];
  }
}
function _parseScaleAsMatrix(...args) {
  if (args.length != 1 && args.length !== 2) return;
  let scaleX = parseFloat(args[0]);
  let scaleY = args.length > 1 ? parseFloat(args[1]) : scaleX;
  if (isNaN(scaleX) || isNaN(scaleY)) return;
  return [scaleX, 0, 0, 0, scaleY, 0, 0, 0, 1];
}
function _parseMatrixAsMatrix(...args) {
  if (args.length != 6) return;
  return [
    parseFloat(args[0]),
    parseFloat(args[2]),
    parseFloat(args[4]),
    parseFloat(args[1]),
    parseFloat(args[3]),
    parseFloat(args[5]),
    0,
    0,
    1,
  ];
}

function _parseTransformAsMatrix(transform) {
  let index = 0;
  let finalMatrix = IDENTITY_MATRIX;
  let items = [
    [_parseTranslateAsMatrix, 'translate'.length + 1, transform.indexOf('translate', index)],
    [_parseRotateAsMatrix, 'rotate'.length + 1, transform.indexOf('rotate', index)],
    [_parseScaleAsMatrix, 'scale'.length + 1, transform.indexOf('scale', index)],
    //[_parseSkewAsMatrix, 'skew'.length + 1, transform.indexOf('skew', index)],
    [_parseMatrixAsMatrix, 'matrix'.length + 1, transform.indexOf('matrix', index)],
  ];
  items = items.filter((item) => item[2] >= 0);
  items.sort((a, b) => a[2] - b[2]);
  for (let item of items) {
    let parms = transform.substring(item[2] + item[1], transform.indexOf(')', item[2] + item[1])).split(/[\s,]+/);
    let matrix = item[0].apply(this, parms);
    if (matrix) {
      finalMatrix = multiply3x3(finalMatrix, matrix);
    } else {
      console.log('Failed to parse ' + transform.substring(item[2], transform.indexOf(')', item[2]) + item[1]));
    }
  }
  return finalMatrix;
}

const CSS_PIXELS_PER_INCH = 96;
const MM_PER_INCH = 25.4;
const PICAS_PER_INCH = 6;
const POINTS_PER_INCH = 72;
let unitsAsPixels = {
  em: NaN,
  ex: NaN,
  px: 1,
  pt: CSS_PIXELS_PER_INCH / POINTS_PER_INCH,
  pc: CSS_PIXELS_PER_INCH / PICAS_PER_INCH,
  cm: (CSS_PIXELS_PER_INCH * 10) / MM_PER_INCH,
  mm: CSS_PIXELS_PER_INCH / MM_PER_INCH,
  in: CSS_PIXELS_PER_INCH,
};

function _coordinateToPixels(value) {
  let match = /([+-]?([0-9]*[.])?[0-9]+)(px|pt|pc|cm|mm|in)?/.exec(value);
  if (!match) {
    return undefined;
  }
  let units = match[3] ? unitsAsPixels[match[3]] : unitsAsPixels['px'];
  if (isNaN(units)) {
    return undefined;
  }
  return parseFloat(match[1]) * units;
}

function _viewboxMatrix([ex, ey, ew, eh], [vbx, vby, vbw, vbh], [align, meetOrSlice]) {
  // Protect against missing data
  ew = typeof ew === 'undefined' || isNaN(ew) ? 300 : ew;
  eh = typeof eh === 'undefined' || isNaN(eh) ? 150 : eh;
  vbx = typeof vbx === 'undefined' || isNaN(vbx) ? 0 : vbx;
  vby = typeof vby === 'undefined' || isNaN(vby) ? 0 : vby;
  vbw = typeof vbw === 'undefined' || isNaN(vbw) ? ew : vbw;
  vbh = typeof vbh === 'undefined' || isNaN(vbh) ? eh : vbh;
  // Calculate scale
  let scaleX = ew / vbw;
  let scaleY = eh / vbh;
  if (align !== 'none') {
    let scale = meetOrSlice === 'meet' ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY);
    scaleX = scale;
    scaleY = scale;
  }
  // Calculate translation
  let translateX = ex - vbx * scaleX;
  let translateY = ey - vby * scaleY;
  if (-1 !== align.indexOf('xMid')) translateX = translateX + (ew - vbw * scaleX) / 2;
  if (-1 !== align.indexOf('xMax')) translateX = translateX + (ew - vbw * scaleX);
  if (-1 !== align.indexOf('yMid')) translateY = translateY + (eh - vbh * scaleY) / 2;
  if (-1 !== align.indexOf('yMax')) translateY = translateY + (eh - vbh * scaleY);
  let result = _parseTransformAsMatrix(`translate(${translateX},${translateY}) scale(${scaleX},${scaleY})`);

  return result;
}

function _collectConnectionPoint(document, group, clazz, matrices, defaultConnectionGroup) {
  let tspans = group.getElementsByTagName('tspan');
  let circles = group.getElementsByTagName('circle');
  // Bail if it doesn't follow the rules
  let invalidTSpans = !tspans || tspans.length !== 1;
  let invalidCircles = !circles || circles.length !== 1;
  if (invalidTSpans || invalidCircles) {
    let element = invalidTSpans ? (invalidCircles ? 'text and circle' : 'text') : 'circle';
    console.log('Missing ' + element + ' on ' + clazz + ' connection point.');
    return;
  }
  let connectionGroup = group.hasAttribute('data-connection-group')
    ? group.getAttribute('data-connection-group')
    : defaultConnectionGroup;
  let cx = _coordinateToPixels(circles[0].getAttribute('cx'));
  let cy = _coordinateToPixels(circles[0].getAttribute('cy'));
  let relevantTransforms = [];
  for (let currentParent = group; currentParent.localName !== 'svg'; currentParent = currentParent.parentNode) {
    if (currentParent.hasAttribute('transform')) {
      relevantTransforms.push(currentParent.getAttribute('transform')); // put transform on list, with the deepest one last
    }
  }
  let applicableMatrices = [].concat(
    // Transforms in order from bottom to top...
    relevantTransforms.map((transform) => _parseTransformAsMatrix(transform)),
    matrices
  );
  return {
    type: clazz,
    name: connectionGroup,
    position: transformCoordinates([cx, cy], applicableMatrices),
  };
}
